import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import React from "react";
import { format } from "date-fns";
import { styled } from "@mui/material/styles";
import {
  type GridColDef,
  type GridFilterModel,
  type GridRenderCellParams,
  type GridSelectionModel,
  type GridSortModel,
} from "@mui/x-data-grid-pro";
import { EmailManagementListDataGrid } from "../../../components/DataGrid";
import {
  type DataGridRow,
  type StoredPicturesDownloadCondition,
  type RightsSectionOtsukuFeedGridProps,
} from "../types";
import {
  PictureTypeLabels,
  PictureTypeValues,
  RealEstateTypeLabels,
  RealEstateTypeValues,
} from "../types/enums";
import { Button, IconButton } from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { ButtonVariantOption, LinkButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { downloadStoredPicturesAPI } from "@/api/downloadStoredPicturesAPI";
import { type AxiosInstance } from "axios";
import { type NavigateFunction } from "react-router-dom";
import { CustomColumnMenu } from "./CustomColumnMenu";

// 種別カラムのフィルタ用選択肢（singleSelect型で日本語ラベルのドロップダウンを表示するために使用）
const realEstateTypeOptions = [
  { value: RealEstateTypeValues.LAND, label: RealEstateTypeLabels.LAND },
  {
    value: RealEstateTypeValues.BUILDING,
    label: RealEstateTypeLabels.BUILDING,
  },
  {
    value: RealEstateTypeValues.CONDOMINIUM,
    label: RealEstateTypeLabels.CONDOMINIUM,
  },
];

// 事項種別カラムのフィルタ用選択肢（singleSelect型で日本語ラベルのドロップダウンを表示するために使用）
const pictureTypeOptions = [
  {
    value: PictureTypeValues.SHOYUSHAJIKO,
    label: PictureTypeLabels.SHOYUSHAJIKO,
  },
  { value: PictureTypeValues.ZENBUJIKO, label: PictureTypeLabels.ZENBUJIKO },
];

const StyledDataGrid = styled(EmailManagementListDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

const makeGridColDef = (
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setDownloadCondition: React.Dispatch<
    React.SetStateAction<StoredPicturesDownloadCondition>
  >,
  apiClient: AxiosInstance,
  navigate: NavigateFunction,
  isReadOnly: boolean
): GridColDef[] => {
  // ダウンロードボタンクリック時に実行。
  function handleClick(row: DataGridRow): void {
    if (row.receivedDate == null) return;
    if (
      row.lastUserAcquisitionDate === null ||
      row.lastUserAcquisitionDate < row.receivedDate
    ) {
      // lastUserAcquisitionDateがnullまたはreceivedDateより前の日付ならば、モーダル表示。
      setIsOpen(true);
      setDownloadCondition({
        billableData: [
          {
            id: row.id,
            price: row.chargeAmount,
            receivedId: row.receivedId,
          },
        ],
        noBillableData: [],
      });
    } else {
      // モーダルを表示せず無料でダウンロード。
      (async () => {
        await downloadStoredPicturesAPI(
          apiClient,
          {
            billableData: [],
            noBillableData: [
              {
                id: row.id,
              },
            ],
          },
          navigate
        );
      })();
    }
  }

  return [
    {
      field: "transitionButton",
      headerName: "詳細画面",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }: GridRenderCellParams<boolean, DataGridRow>) => {
        if (row.lastUserAcquisitionDate != null) {
          return (
            <LinkButton
              href={`/acquirebookstatus/${row.pictureRequestId}/`}
              variant={ButtonVariantOption.Text}
              wrapperSize={SizingWrapperStyle.SMALL}
            >
              詳細を開く
            </LinkButton>
          );
        } else {
          return (
            <Button variant="text" size="small" disabled>
              詳細を開く
            </Button>
          );
        }
      },
    },
    // singleSelect型にすることで、フィルタ時に日本語ラベル（土地/建物/区分建物）のドロップダウンで選択できるようにする
    {
      field: "realEstateType",
      headerName: "種別",
      width: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: realEstateTypeOptions,
      valueFormatter: (params) => {
        const option = realEstateTypeOptions.find(
          (item) => item.value === params.value
        );
        return option?.label ?? "";
      },
    },
    // singleSelect型にすることで、フィルタ時に日本語ラベル（所有者事項/全部事項）のドロップダウンで選択できるようにする
    {
      field: "pictureType",
      headerName: "事項種別",
      width: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: pictureTypeOptions,
      valueFormatter: (params) => {
        const option = pictureTypeOptions.find(
          (item) => item.value === params.value
        );
        return option?.label ?? "";
      },
    },
    {
      field: "prefectureName",
      headerName: "都道府県",
      width: 150,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "location",
      headerName: "所在",
      width: 300,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "chibanKaoku",
      headerName: "地番または家屋番号",
      width: 300,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    // date型にすることで、フィルタ時に日付ピッカーで日付を選択できるようにする
    // valueGetterでDate型に変換し、フィルタ・ソートが正しく動作するようにする
    {
      field: "receivedDate",
      headerName: "リユース登記",
      width: 180,
      sortable: true,
      align: "center",
      headerAlign: "center",
      type: "date",
      valueGetter: (params) => {
        const row = params.row as DataGridRow;
        if (
          row.receivedDate == null ||
          row.receivedDate === row.lastUserAcquisitionDate
        ) {
          return null;
        }
        return new Date(row.receivedDate);
      },
      valueFormatter: (params) => {
        if (params.value == null) return "";
        return format(params.value as Date, "yyyy/MM/dd");
      },
    },
    // boolean型にすることで、フィルタ時にチェックボックスでダウンロード可否を絞り込めるようにする
    // valueGetterでダウンロード可否を算出し、renderCellでアイコン表示を行う
    {
      field: "isDownloadable",
      headerName: "ダウンロード",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
      type: "boolean",
      valueGetter: (params) => {
        const row = params.row as DataGridRow;
        return (
          row.receivedDate != null &&
          (row.lastUserAcquisitionDate === null ||
            row.lastUserAcquisitionDate < row.receivedDate)
        );
      },
      renderCell: ({ row }: GridRenderCellParams<boolean, DataGridRow>) => {
        const isDisplay =
          row.receivedDate != null &&
          (row.lastUserAcquisitionDate === null ||
            row.lastUserAcquisitionDate < row.receivedDate);
        return isDisplay ? (
          <ReadOnlyTooltip isReadOnly={isReadOnly}>
            <IconButton
              aria-label="download-icon"
              color="primary"
              disabled={isReadOnly}
              onClick={() => {
                handleClick(row);
              }}
            >
              <CloudDownloadIcon />
            </IconButton>
          </ReadOnlyTooltip>
        ) : (
          <></>
        );
      },
    },
    // date型にすることで、フィルタ時に日付ピッカーで日付を選択できるようにする
    // valueGetterでDate型に変換し、フィルタ・ソートが正しく動作するようにする
    {
      field: "lastUserAcquisitionDate",
      headerName: "取得済登記",
      width: 180,
      sortable: true,
      align: "center",
      headerAlign: "center",
      type: "date",
      valueGetter: (params) => {
        if (params.value == null) return null;
        return new Date(params.value as string);
      },
      valueFormatter: (params) => {
        if (params.value == null) return "";
        return format(params.value as Date, "yyyy/MM/dd");
      },
    },
  ];
};

export const RightsSectionOtsukuFeedGrid: React.FC<
  RightsSectionOtsukuFeedGridProps
> = ({
  rows,
  setDownloadCondition,
  setIsOpen,
  apiClient,
  setSelectedRowIds,
  navigate,
  loading,
  isReadOnly,
}) => {
  const colDef = makeGridColDef(
    setIsOpen,
    setDownloadCondition,
    apiClient,
    navigate,
    isReadOnly
  );
  const [selectionModel, setSelectionModel] =
    React.useState<GridSelectionModel>([]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: [],
  });
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [page, setPage] = React.useState(0);

  // 検索ボタン押下時（rows更新時）にフィルタ・ソート・選択・ページをリセット
  React.useEffect(() => {
    setFilterModel({ items: [] });
    setSortModel([]);
    setSelectionModel([]);
    setSelectedRowIds([]);
    setPage(0);
  }, [rows]);

  return (
    <StyledDataGrid
      stickyHeader={true}
      autoHeight
      checkboxSelection
      checkboxSelectionVisibleOnly
      columns={colDef}
      disableColumnMenu={false}
      disableSelectionOnClick={true}
      rows={rows}
      components={{
        ColumnMenu: CustomColumnMenu,
      }}
      page={page}
      onPageChange={setPage}
      filterModel={filterModel}
      onFilterModelChange={setFilterModel}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      selectionModel={selectionModel}
      loading={loading}
      onSelectionModelChange={(newSelectionModel: GridSelectionModel) => {
        setSelectedRowIds(newSelectionModel);
        setSelectionModel(newSelectionModel);
      }}
    />
  );
};
